import React from "react";
import { useHistory } from "react-router-dom";

import {
  Edit,
  Loading,
  required,
  SimpleForm,
  useEditController,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FormColumns } from "../../../components/common/FormColumns";
import { FormSection } from "../../../components/common/FormSection";
import {
  ScheduleDateTimeInput,
  useScheduleEventType,
} from "../../../components/common/scheduler/ScheduleDateTimeInput";
import { Workflow } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";

import { useInboundScheduleGuard } from "../dispatchGuard";
import {
  DispatchEditActions,
  DispatchEditToolbar,
} from "./InboundDispatchEdit";
import { InboundAside } from "./InboundAside";
import { getInboundExceptions, ShipmentSetupTab } from "./InboundShow";

const validateRequired = required();

/**
 * Where a carrier lands immediately after claiming a delivery: the setup fields
 * it just searched on beside the calendar, on one page, so claim-and-schedule
 * stays a single motion — the delivery on the left to check against, the
 * scheduler on the right, no dialog between the carrier and a time. Opening the
 * same delivery from the deliveries list gets the full InboundDispatchEdit
 * instead — CarrierApp picks between the two off the claim's navigation state.
 */
export const InboundDispatchScheduleEdit = (props) => {
  const { record } = useEditController(props);
  const { appUser } = useUser();
  const history = useHistory();
  const backPath = getBackPath(props);
  const { eventType, canSchedule, selectedTimeslot, setSelectedTimeslot } =
    useScheduleEventType(
      record,
      Workflow.INBOUND,
      record?.account?.inboundEventTypeId,
    );
  const blocked = useInboundScheduleGuard(record, props.basePath);

  if (blocked || !record?.account) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Schedule Delivery" />}
      mutationMode="pessimistic"
      actions={<DispatchEditActions {...props} backPath={backPath} />}
      aside={
        <InboundAside
          {...props}
          history={history}
          backPath={backPath}
          exceptions={getInboundExceptions(record)}
        />
      }
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <DispatchEditToolbar
            {...props}
            record={record}
            backPath={backPath}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormColumns>
          <FormSection title="Inbound Info">
            <ShipmentSetupTab {...props} />
          </FormSection>
          <FormSection title="Schedule Delivery">
            <ScheduleDateTimeInput
              inline
              id="schedule-input"
              label="Delivery Appointment"
              account={record.account}
              workflow={Workflow.INBOUND}
              eventType={eventType}
              canSchedule={canSchedule}
              selectedTimeslot={selectedTimeslot}
              setSelectedTimeslot={setSelectedTimeslot}
              validate={validateRequired}
              fullWidth={true}
              helperText={
                canSchedule
                  ? false
                  : `Contact ${appUser?.reloadYard?.name} to setup an event type.`
              }
            />
          </FormSection>
        </FormColumns>
      </SimpleForm>
    </Edit>
  );
};
